import { useLingui } from "@lingui/react/macro";
import { format } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { useCampground, useCurrency } from "@/contexts/AppConfigContext";
import { api } from "@/services/api";
import type { PriceCheckResponse } from "@/types/api";
import type { SelectedExtra } from "@/types/booking";
import { logError } from "@/utils/logError";
import { formatCurrency } from "@/utils/pricing";

export interface CheckPriceParams {
  dateFrom: Date;
  dateUntil: Date;
  campsiteId: number;
  persons: Record<number, number>;
  extras?: SelectedExtra[];
}

interface UsePriceCheckReturn {
  loading: boolean;
  price: PriceCheckResponse | null;
  checkPrice: (params: CheckPriceParams) => Promise<PriceCheckResponse>;
  formatPrice: (value: number) => string | null;
  formatPriceAlt: (value: number) => string | null;
  amountToPayOnline: number;
}

export function usePriceCheck(): UsePriceCheckReturn {
  const { data: campgroundData } = useCampground();
  const { currencyCode } = useCurrency();
  const { i18n } = useLingui();

  const [price, setPrice] = useState<PriceCheckResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const checkPrice = useCallback(
    async (params: CheckPriceParams) => {
      setPrice(null);
      setLoading(true);

      try {
        const dateFromStr = format(params.dateFrom, "yyyy-MM-dd");
        const dateUntilStr = format(params.dateUntil, "yyyy-MM-dd");

        const response = await api.checkPrice(
          campgroundData.urls?.price_check,
          {
            currency_alt: currencyCode,
          },
          {
            date_from: dateFromStr,
            date_until: dateUntilStr,
            campsites: [
              {
                id: params.campsiteId,
                amount: 1,
                persons: Object.entries(params.persons).map(([id, amount]) => ({
                  id: parseInt(id, 10),
                  amount,
                })),
              },
            ],
            extras: params.extras ?? [],
          },
        );

        setPrice(response);
        setLoading(false);
        return response;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logError(error);
        setLoading(false);
        throw error;
      }
    },
    [campgroundData, currencyCode],
  );

  const formatPrice = useCallback(
    (value: number) => {
      if (!price?.currency) return null;
      return formatCurrency(value, price.currency, i18n.locale);
    },
    [price, i18n.locale],
  );

  const formatPriceAlt = useCallback(
    (value: number) => {
      if (!price?.currency_alt?.currency) return null;
      return formatCurrency(value, price.currency_alt.currency, i18n.locale);
    },
    [price, i18n.locale],
  );

  const amountToPayOnline = useMemo(() => {
    if (!price) return 0;
    if (price.deposit_percent && price.deposit_value < price.total) {
      return price.deposit_value;
    }
    return price.total;
  }, [price]);

  return {
    loading,
    price,
    checkPrice,
    formatPrice,
    formatPriceAlt,
    amountToPayOnline,
  };
}
